import Sidebar from "../../../../components/ui/sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "../../../../components/ui/card";
import { useState, useEffect } from "react";
import { useAuth } from "../../../../context/AuthContext";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Eye, EyeOff, Lock, CheckCircle, XCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

      import { ChangePasswordRequest, changePassword } from "../../../../api/usuarioServices";

interface PasswordRequirement {
  text: string;
  regex: RegExp;
  met: boolean;
}

export default function ChangePasswordPage() {
  const { session } = useAuth();
  const navigate = useNavigate();
  
  // Obtener la contraseña guardada (si existe)
  const savedPassword = session?.password || "";
  
  const [passwordData, setPasswordData] = useState({
    currentPassword: savedPassword, // Pre-llenar con la contraseña guardada
    newPassword: "",
    confirmPassword: "",
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [passwordRequirements, setPasswordRequirements] = useState<PasswordRequirement[]>([
    { text: "Mínimo 8 caracteres", regex: /.{8,}/, met: false },
    { text: "Al menos una mayúscula", regex: /[A-Z]/, met: false },
    { text: "Al menos un número", regex: /\d/, met: false },
    { text: "Al menos una minúscula", regex: /[a-z]/, met: false },
  ]);

  // Actualizar validaciones de contraseña en tiempo real
  useEffect(() => {
    const newPassword = passwordData.newPassword;
    setPasswordRequirements(prev => 
      prev.map(req => ({
        ...req,
        met: req.regex.test(newPassword)
      }))
    );
  }, [passwordData.newPassword]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({ ...prev, [name]: value }));
  };

  const togglePasswordVisibility = (field: 'current' | 'new' | 'confirm') => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const isPasswordValid = () => {
    return passwordRequirements.every(req => req.met);
  };

  const handleSubmit = async () => {
    try {
      // Validaciones básicas
      if (!passwordData.currentPassword) {
        Swal.fire({
          icon: "warning",
          title: "Campo requerido",
          text: "Por favor, ingrese su contraseña actual",
          confirmButtonColor: "#3085d6",
        });
        return;
      }

      if (!passwordData.newPassword) {
        Swal.fire({
          icon: "warning",
          title: "Campo requerido",
          text: "Por favor, ingrese una nueva contraseña",
          confirmButtonColor: "#3085d6",
        });
        return;
      }

      if (!isPasswordValid()) {
        Swal.fire({
          icon: "warning",
          title: "Contraseña no válida",
          text: "La nueva contraseña no cumple con los requisitos de seguridad",
          confirmButtonColor: "#3085d6",
        });
        return;
      }

      if (passwordData.newPassword !== passwordData.confirmPassword) {
        Swal.fire({
          icon: "warning",
          title: "Contraseñas no coinciden",
          text: "La nueva contraseña y la confirmación no coinciden",
          confirmButtonColor: "#3085d6",
        });
        return;
      }

      if (passwordData.currentPassword === passwordData.newPassword) {
        Swal.fire({
          icon: "warning",
          title: "Contraseña igual",
          text: "La nueva contraseña debe ser diferente a la actual",
          confirmButtonColor: "#3085d6",
        });
        return;
      }

      // Validar que session tenga los datos necesarios
      if (!session?.email || !session?.apartamento?.id || !session.rol?.id) {
        Swal.fire({
          icon: "error",
          title: "Error de sesión",
          text: "No se encontró la información de sesión necesaria",
          confirmButtonColor: "#d33",
        });
        return;
      }

      setIsLoading(true);

      // Preparar el payload para el servicio
      const payload: ChangePasswordRequest = {
        email: session.email,
        "Codigo apartamento": session.apartamento?.id,
        "Tipo usuario": String(session.rol?.id),
        "Contrasena anterior": passwordData.currentPassword,
        "Contrasena nueva": passwordData.newPassword,
        "Contrasena nueva confirmacion": passwordData.confirmPassword,
      };
      // Llamar al servicio
      const response = await changePassword(payload);

      // Verificar el código de respuesta
      if (response.code === "1" || response.code === 1) {
        // Éxito
        Swal.fire({
          icon: "success",
          title: "¡Éxito!",
          text: response.message,
          confirmButtonColor: "#3085d6",
          timer: 2000,
          timerProgressBar: true,
        }).then(() => {
          // Limpiar el formulario
          setPasswordData({
            currentPassword: "",
            newPassword: "",
            confirmPassword: "",
          });
          // Volver a la página anterior
          navigate(-1);
        });
      } else {
        // Error del servidor
        Swal.fire({
          icon: "error",
          title: "Error",
          text: response.message || "No se pudo cambiar la contraseña",
          confirmButtonColor: "#d33",
        });
      }

    } catch (error: any) {
      console.error("Error al cambiar contraseña:", error);
      
      // Mostrar mensaje de error
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error?.message || "Ocurrió un error al cambiar la contraseña. Por favor, intente nuevamente.",
        confirmButtonColor: "#d33",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(-1); // Volver a la página anterior
  };

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-gray-100 via-gray-200 to-gray-300 text-gray-900">
      {/* Sidebar */}
      <Sidebar />

      {/* Contenido principal */}
      <main className="flex-1 p-6 md:ml-64 transition-all">
        {/* Header */}
        <div className="mb-6">
          <h2 className="text-3xl font-bold text-military-600">Cambiar Contraseña</h2>
        </div>

        {/* Layout responsivo: columnas en desktop, apilado en móvil */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Formulario - 2/3 del ancho en desktop */}
          <div className="lg:col-span-2">
            <Card className="shadow-xl border-l-4 border-military-500 rounded-lg bg-white">
              <CardHeader>
                <CardTitle className="text-xl text-military-600 flex items-center gap-2">
                  <Lock size={24} />
                  Actualizar Contraseña
                </CardTitle>
                <p className="text-gray-600 text-sm mt-2">
                  Por favor, ingrese su contraseña actual y la nueva contraseña que desea establecer.
                </p>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {/* Contraseña actual */}
                  <div>
                    <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700 mb-1">
                      Contraseña Actual *
                    </label>
                    <div className="relative">
                      <Input
                        id="currentPassword"
                        name="currentPassword"
                        type={showPasswords.current ? "text" : "password"}
                        value={passwordData.currentPassword}
                        onChange={handleInputChange}
                        placeholder="Ingrese su contraseña actual"
                        className="w-full bg-white border-gray-300 focus:border-military-500 focus:ring-military-500 rounded-lg pr-10"
                        disabled={isLoading}
                      />
                      <button
                        type="button"
                        onClick={() => togglePasswordVisibility('current')}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                        disabled={isLoading}
                      >
                        {showPasswords.current ? <EyeOff size={20} /> : <Eye size={20} />}
                      </button>
                    </div>
                  </div>

                  {/* Nueva contraseña */}
                  <div>
                    <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">
                      Nueva Contraseña *
                    </label>
                    <div className="relative">
                      <Input
                        id="newPassword"
                        name="newPassword"
                        type={showPasswords.new ? "text" : "password"}
                        value={passwordData.newPassword}
                        onChange={handleInputChange}
                        placeholder="Ingrese su nueva contraseña"
                        className="w-full bg-white border-gray-300 focus:border-military-500 focus:ring-military-500 rounded-lg pr-10"
                        disabled={isLoading}
                      />
                      <button
                        type="button"
                        onClick={() => togglePasswordVisibility('new')}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                        disabled={isLoading}
                      >
                        {showPasswords.new ? <EyeOff size={20} /> : <Eye size={20} />}
                      </button>
                    </div>
                  </div>

                  {/* Requisitos de contraseña */}
                  {passwordData.newPassword && (
                    <div className="bg-gray-50 p-4 rounded-lg border">
                      <h4 className="text-sm font-medium text-gray-700 mb-3">Requisitos de contraseña:</h4>
                      <div className="space-y-2">
                        {passwordRequirements.map((req, index) => (
                          <div key={index} className="flex items-center gap-2">
                            {req.met ? (
                              <CheckCircle size={16} className="text-green-500" />
                            ) : (
                              <XCircle size={16} className="text-red-500" />
                            )}
                            <span className={`text-sm ${req.met ? 'text-green-700' : 'text-red-600'}`}>
                              {req.text}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Confirmar contraseña */}
                  <div>
                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                      Confirmar Nueva Contraseña *
                    </label>
                    <div className="relative">
                      <Input
                        id="confirmPassword"
                        name="confirmPassword"
                        type={showPasswords.confirm ? "text" : "password"}
                        value={passwordData.confirmPassword}
                        onChange={handleInputChange}
                        placeholder="Confirme su nueva contraseña"
                        className="w-full bg-white border-gray-300 focus:border-military-500 focus:ring-military-500 rounded-lg pr-10"
                        disabled={isLoading}
                      />
                      <button
                        type="button"
                        onClick={() => togglePasswordVisibility('confirm')}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                        disabled={isLoading}
                      >
                        {showPasswords.confirm ? <EyeOff size={20} /> : <Eye size={20} />}
                      </button>
                    </div>
                    {passwordData.confirmPassword && passwordData.newPassword !== passwordData.confirmPassword && (
                      <p className="text-red-600 text-sm mt-1">Las contraseñas no coinciden</p>
                    )}
                  </div>

                  {/* Botones */}
                  <div className="flex flex-col sm:flex-row gap-4 pt-4">
                    <Button
                      onClick={handleSubmit}
                      disabled={isLoading || !isPasswordValid() || passwordData.newPassword !== passwordData.confirmPassword}
                      className="flex-1 bg-military-500 hover:bg-military-600 text-white rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isLoading ? (
                        <div className="flex items-center gap-2">
                          <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                          Cambiando...
                        </div>
                      ) : (
                        "Cambiar Contraseña"
                      )}
                    </Button>
                    <Button
                      onClick={handleCancel}
                      variant="outline"
                      disabled={isLoading}
                      className="flex-1 border-gray-300 hover:bg-gray-50 rounded-lg transition"
                    >
                      Cancelar
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Recomendaciones de seguridad - 1/3 del ancho en desktop, abajo en móvil */}
          <div className="lg:col-span-1">
            <Card className="bg-blue-50 border-blue-200 h-fit">
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <Lock size={20} className="text-blue-600 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium text-blue-900 mb-3">Recomendaciones de seguridad</h4>
                    <ul className="text-sm text-blue-800 space-y-2">
                      <li className="flex items-start gap-2">
                        <span className="text-blue-600 mt-1">•</span>
                        <span>Use una contraseña única que no utilice en otros sitios</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-blue-600 mt-1">•</span>
                        <span>Evite información personal fácil de adivinar</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-blue-600 mt-1">•</span>
                        <span>Considere usar un administrador de contraseñas</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-blue-600 mt-1">•</span>
                        <span>Cambie su contraseña regularmente</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-blue-600 mt-1">•</span>
                        <span>No comparta su contraseña con nadie</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}